import { useEffect, useState } from "react";
import { Building2, Link2 } from "lucide-react";
import axios from "src/common/axiosInstance";
import { autoAppyUrl } from "src/config/autoAppyUrls";
import "./WorkspaceOrganisationConnection.css";

export default function WorkspaceOrganisationConnection({
  workspace,
  onConnected,
}) {
  const [data, setData] = useState(null);
  const [organisation, setOrganisation] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const [review, setReview] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewRevision, setReviewRevision] = useState(0);
  const [useCentralTeam, setUseCentralTeam] = useState(false);
  const endpoint = `/workspaces/${workspace.id}/organisation-link`;
  const currentReview = review?.organisation === organisation && review?.endpoint === endpoint ? review : null;

  useEffect(() => {
    let current = true;
    setData(null);
    setError("");
    axios
      .get(endpoint)
      .then(({ data: response }) => {
        if (!current) return;
        setData(response.data);
        // Always require a deliberate choice when automatic matching was not safe.
        setOrganisation("");
      })
      .catch((e) => {
        if (current)
          setError(
            e.response?.data?.message || "Unable to load your organisations.",
          );
      });
    return () => {
      current = false;
    };
  }, [endpoint, revision]);

  useEffect(() => {
    let current = true;
    setReview(null);
    setUseCentralTeam(false);
    if (!organisation) return;
    setReviewLoading(true);
    setError("");
    axios.get(`${endpoint}/preview`, { params: { organisation_id: organisation } })
      .then(({ data: response }) => {
        if (current) setReview({ ...response.data, organisation, endpoint });
      })
      .catch((e) => {
        if (current) setError(e.response?.data?.message || "Unable to review team access.");
      })
      .finally(() => { if (current) setReviewLoading(false); });
    return () => { current = false; };
  }, [endpoint, organisation, reviewRevision]);

  const connect = async () => {
    if (!currentReview || (currentReview.requires_review && !useCentralTeam)) return;
    setBusy(true);
    setError("");
    try {
      await axios.post(endpoint, {
        organisation_id: organisation,
        ...(useCentralTeam ? { team_source: "autoappy", review_token: currentReview.review_token } : {}),
      });
      await onConnected();
    } catch (e) {
      setReview(null);
      setUseCentralTeam(false);
      setError(
        e.response?.data?.message || "Unable to connect this workspace.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="customer-share customer-share-settings">
      <header>
        <span className="customer-share-icon">
          <Building2 size={21} />
        </span>
        <div>
          <p className="customer-share-eyebrow">ONE ORGANISATION</p>
          <h2>Connect your CRM workspace</h2>
        </div>
      </header>
      <div className="customer-share-body">
        <p className="customer-share-intro">
          Choose the Auto Appy organisation that owns{" "}
          {workspace.name || "this workspace"}.
        </p>
        <p className="customer-share-muted">
          Your leads, pipelines, documents and assignments stay here. Connecting
          enables your central team and customer-sharing controls; it does not
          share existing leads.
        </p>
        {error && (
          <div className="customer-share-error" role="alert">
            {error}
            <button
              type="button"
              disabled={busy}
              onClick={() => organisation ? setReviewRevision((v) => v + 1) : setRevision((v) => v + 1)}
            >
              {organisation ? "Refresh team review" : "Refresh organisations"}
            </button>
          </div>
        )}
        {!data && !error && <p role="status">Loading organisations…</p>}
        {data?.organisation_id ? (
          <button type="button" onClick={onConnected}>
            Open connected workspace
          </button>
        ) : (
          data &&
          (data.can_link ? (
            <>
              <label className="grid gap-2 text-sm font-semibold">
                Auto Appy organisation
                <select
                  className="w-full rounded-xl border border-sky-100 bg-white p-3 text-slate-700"
                  value={organisation}
                  disabled={busy}
                  onChange={(e) => setOrganisation(e.target.value)}
                >
                  <option value="">Select an organisation</option>
                  {data.organisations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </label>
              {!data.organisations.length && (
                <p className="customer-share-muted">
                  You need owner or admin access to an Auto Appy organisation
                  with Sales enabled.
                </p>
              )}
              {organisation && reviewLoading && <p role="status">Reviewing team access…</p>}
              {currentReview && (
                <div className="crm-team-review">
                  <h3>Team access after connection</h3>
                  <p className="customer-share-muted">
                    Auto Appy will manage roles, Sales access and reporting lines. Existing CRM
                    users and their assignments stay in the history. Admins can reassign their leads later.
                  </p>
                  {currentReview.current_team.length > 0 && (
                    <div className="crm-team-review-table" tabIndex={0} role="region" aria-label="CRM team access changes">
                      <table>
                        <thead><tr><th>Current CRM team</th><th>Now</th><th>After connection</th></tr></thead>
                        <tbody>{currentReview.current_team.map((member) => (
                          <tr key={member.user_id}>
                            <td><strong>{member.name}</strong><small>{member.email}</small></td>
                            <td>{member.current_status === "active" ? member.current_role : "Inactive"}</td>
                            <td><span className={member.next_status === "active" ? "crm-team-access-active" : "crm-team-access-inactive"}>
                              {member.next_status === "active" ? member.next_role : "No Sales access"}
                            </span></td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  )}
                  <details className="crm-team-review-directory">
                    <summary>Auto Appy team with Sales access ({currentReview.organisation_team.length})</summary>
                    <ul>{currentReview.organisation_team.map((member) => (
                      <li key={member.email}><strong>{member.name}</strong><span>{member.email}</span>
                        <small>{member.role}{member.reports_to ? ` · Reports to ${member.reports_to}` : ""}</small></li>
                    ))}</ul>
                  </details>
                  {currentReview.requires_review && (
                    <div className="crm-team-review-decision">
                      <strong>Your CRM has a legacy team</strong>
                      <p>Some people will lose CRM access or receive a different role. Their records stay in this workspace.</p>
                      <label>
                        <input type="checkbox" checked={useCentralTeam} disabled={busy} onChange={(e) => setUseCentralTeam(e.target.checked)} />
                        <span>Use the current Auto Appy team. I’ve reviewed the access changes.</span>
                      </label>
                      <p>To keep a legacy teammate active, add them to your Auto Appy organisation with Sales access, then refresh this review.</p>
                      <button type="button" disabled={busy} onClick={() => setReviewRevision((v) => v + 1)}>Refresh team review</button>
                    </div>
                  )}
                </div>
              )}
              <div className="customer-share-actions">
                <button
                  type="button"
                  className="customer-share-primary"
                  disabled={!currentReview || reviewLoading || busy || (currentReview.requires_review && !useCentralTeam)}
                  onClick={connect}
                >
                  <Link2 size={15} />
                  {busy ? "Connecting…" : "Connect organisation"}
                </button>
              </div>
            </>
          ) : (
            <p className="customer-share-muted">
              A CRM admin who also manages your Auto Appy organisation can
              connect this workspace for the whole team.
            </p>
          ))
        )}
        <a
          className="text-sm font-semibold text-sky-700 underline"
          href={autoAppyUrl("/organisation")}
        >
          Manage organisations in Auto Appy
        </a>
      </div>
    </section>
  );
}
